import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  DONATE_DISPLAY_CURRENCIES,
  detectPreferredCurrency,
  fetchUsdRates,
  formatDonateMoney,
} from "../utils/donateCurrency";
import {
  FALLBACK_DONOR_TIERS,
  FALLBACK_SERVER_HOST,
  FALLBACK_NOCTURNE_PASS,
} from "../data/donorDefaults";

const API_BASE = process.env.REACT_APP_API_BASE;

export default function DonatePage({ user, guilds = [] }) {
  const [searchParams] = useSearchParams();
  const [tiers, setTiers] = useState([]);
  const [serverHost, setServerHost] = useState(FALLBACK_SERVER_HOST);
  const [nocturnePass, setNocturnePass] = useState(FALLBACK_NOCTURNE_PASS);
  const [hostGuildId, setHostGuildId] = useState("");
  const [configured, setConfigured] = useState(false);
  const [personalized, setPersonalized] = useState(false);
  const [status, setStatus] = useState(null);
  const [supporters, setSupporters] = useState([]);
  const [supporterCounts, setSupporterCounts] = useState({
    supporter: 0,
    patron: 0,
    benefactor: 0,
  });
  const [supportersLoading, setSupportersLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState("");
  const [displayCurrency, setDisplayCurrency] = useState(() =>
    detectPreferredCurrency(),
  );
  const [fxRates, setFxRates] = useState({ USD: 1 });
  const [fxSource, setFxSource] = useState("fallback");
  const token = localStorage.getItem("kami_token");

  const manageableGuilds = useMemo(
    () => (guilds || []).filter((g) => g.hasBot),
    [guilds],
  );

  const flash = useMemo(() => {
    if (searchParams.get("success") === "1") {
      const tier = searchParams.get("tier");
      const isHost = tier === "server_host";
      const isPass = tier === "nocturne_pass";
      return {
        type: "ok",
        text: isHost
          ? "Thank you. Server Host is unlocking for that server — higher caps and Kami profile are available shortly."
          : isPass
            ? "Thank you. Nocturne Pass premium is unlocking — claim rewards with /level pass."
            : "Thank you. Your support is processing - perks appear on your profile shortly.",
      };
    }
    if (searchParams.get("canceled") === "1") {
      return {
        type: "muted",
        text: "Checkout canceled. You can try again whenever you're ready.",
      };
    }
    return null;
  }, [searchParams]);

  const fxRate = Number(fxRates[displayCurrency] || 1);

  useEffect(() => {
    let cancelled = false;
    fetchUsdRates().then((result) => {
      if (cancelled) return;
      setFxRates(result.rates || { USD: 1 });
      setFxSource(result.source || "fallback");
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem("nocturne_donate_currency", displayCurrency);
    } catch {
      // ignore
    }
  }, [displayCurrency]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const headers = token
          ? { Authorization: `Bearer ${token}` }
          : undefined;
        const sessionId = searchParams.get("session_id");
        if (token && sessionId && searchParams.get("success") === "1") {
          try {
            await axios.post(
              `${API_BASE}/donate/confirm`,
              { sessionId },
              { headers: { Authorization: `Bearer ${token}` } },
            );
          } catch (confirmErr) {
            console.error("donate confirm failed", confirmErr);
          }
        }
        const { data } = await axios.get(`${API_BASE}/donate/tiers`, {
          headers,
        });
        if (cancelled) return;
        setTiers(
          Array.isArray(data.tiers) && data.tiers.length
            ? data.tiers
            : FALLBACK_DONOR_TIERS,
        );
        setServerHost(
          data.serverHost && data.serverHost.id
            ? data.serverHost
            : FALLBACK_SERVER_HOST,
        );
        setNocturnePass(
          data.nocturnePass && data.nocturnePass.id
            ? data.nocturnePass
            : FALLBACK_NOCTURNE_PASS,
        );
        setConfigured(Boolean(data.configured));
        setPersonalized(Boolean(data.personalized));
        if (token) {
          try {
            const me = await axios.get(`${API_BASE}/donate/me`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (!cancelled) setStatus(me.data);
          } catch {
            if (!cancelled) setStatus(null);
          }
        }
        setError("");
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || "Could not load support options.");
          setTiers(FALLBACK_DONOR_TIERS);
          setServerHost(FALLBACK_SERVER_HOST);
          setNocturnePass(FALLBACK_NOCTURNE_PASS);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [token, searchParams]);

  const successFlag = searchParams.get("success");
  const [wallBusy, setWallBusy] = useState(false);

  const loadSupporters = async () => {
    setSupportersLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/donate/supporters`);
      setSupporters(Array.isArray(data.supporters) ? data.supporters : []);
      setSupporterCounts({
        supporter: Number(data.counts?.supporter || 0),
        patron: Number(data.counts?.patron || 0),
        benefactor: Number(data.counts?.benefactor || 0),
      });
    } catch {
      setSupporters([]);
      setSupporterCounts({ supporter: 0, patron: 0, benefactor: 0 });
    } finally {
      setSupportersLoading(false);
    }
  };

  useEffect(() => {
    loadSupporters();
  }, [successFlag]);

  const showWallToggle =
    user &&
    status &&
    (status.hiddenFromWall ||
      status.donorSupporter ||
      status.donorTier !== "none" ||
      status.donationTotalCents > 0);

  const setWallVisibility = async (hidden) => {
    if (!token) return;
    setWallBusy(true);
    setError("");
    try {
      await axios.post(
        `${API_BASE}/donate/wall-visibility`,
        { hidden },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const me = await axios.get(`${API_BASE}/donate/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus(me.data);
      await loadSupporters();
    } catch (err) {
      setError(
        err.response?.data?.error ||
          "Could not update supporters wall visibility.",
      );
    } finally {
      setWallBusy(false);
    }
  };

  const startCheckout = async (tierId, guildId = null) => {
    if (!user || !token) {
      window.location.href = `${API_BASE}/auth/login`;
      return;
    }
    if (tierId === "server_host" && !guildId) {
      setError("Choose which server should receive Server Host.");
      return;
    }
    setBusy(tierId);
    setError("");
    try {
      const body = { tierId };
      if (guildId) body.guildId = guildId;
      const { data } = await axios.post(`${API_BASE}/donate/checkout`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      setError("Checkout did not return a URL.");
    } catch (err) {
      setError(err.response?.data?.error || "Could not start checkout.");
    } finally {
      setBusy(null);
    }
  };

  const openPortal = async () => {
    if (!token) return;
    setBusy("portal");
    setError("");
    try {
      const { data } = await axios.post(
        `${API_BASE}/donate/portal`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (data.url) window.location.href = data.url;
    } catch (err) {
      setError(err.response?.data?.error || "Could not open billing portal.");
    } finally {
      setBusy(null);
    }
  };

  const priceFor = (tier) => {
    const label = formatDonateMoney(
      tier.amountCents,
      displayCurrency,
      fxRate,
    );
    return tier.kind === "subscription" ? `${label}/mo` : label;
  };

  const formatSince = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return null;
    }
  };

  return (
    <div className="landing-page donate-page">
      <div className="landing-inner">
        <section className="landing-hero">
          <p className="landing-eyebrow">☾ Support Nocturne</p>
          <h1>Keep the quiet lit</h1>
          <p className="landing-lead">
            Support yourself with badges, titles, and <code>/patron</code>{" "}
            utilities — or add <strong>Server Host</strong> once for a single
            Discord server (optional branding extras like Kami&apos;s
            per-server profile). Core server tools stay free. Discord boosts
            stay separate.
          </p>
          <div className="landing-cta">
            {!user && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  window.location.href = `${API_BASE}/auth/login`;
                }}
              >
                Log in with Discord
              </button>
            )}
            {user && status?.hasStripeCustomer && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={openPortal}
                disabled={busy === "portal"}
              >
                {busy === "portal" ? "Opening..." : "Manage billing"}
              </button>
            )}
            <Link to="/profile" className="btn btn-secondary">
              View profile
            </Link>
          </div>
        </section>

        {flash && (
          <p
            className={
              flash.type === "ok" ? "donate-flash donate-flash-ok" : "donate-flash"
            }
          >
            {flash.text}
          </p>
        )}
        {error && <p className="donate-flash donate-flash-error">{error}</p>}

        {user && status && (
          <p className="portal-muted donate-status">
            Your support:{" "}
            <strong>
              {status.donorTier === "none"
                ? "None yet"
                : String(status.donorTier).replace(/^\w/, (c) =>
                    c.toUpperCase(),
                  )}
            </strong>
            {status.subscriptionStatus &&
              status.subscriptionStatus !== "none" && (
                <> · Subscription: {status.subscriptionStatus}</>
              )}
            {status.donationTotalCents > 0 && (
              <>
                {" "}
                · Lifetime{" "}
                {formatDonateMoney(
                  status.donationTotalCents,
                  displayCurrency,
                  fxRate,
                )}
              </>
            )}
            {personalized && (
              <>
                {" "}
                · Gifts scaled to level{" "}
                {Number(status.level || 1).toLocaleString()} ·{" "}
                {Number(status.essence || 0).toLocaleString()} Essence
              </>
            )}
            {Array.isArray(status.badges) &&
              status.badges.includes("Kami Supporter") && (
                <> · Badge: Kami Supporter</>
              )}
            {status.activeTitle && status.activeTitle !== "Seeker" && (
              <> · Title: {status.activeTitle}</>
            )}
          </p>
        )}
        {showWallToggle && (
          <p className="donate-wall-toggle-wrap">
            {status.hiddenFromWall ? (
              <>
                You are hidden from the public supporters list.{" "}
                <button
                  type="button"
                  className="donate-wall-toggle"
                  disabled={wallBusy}
                  onClick={() => setWallVisibility(false)}
                >
                  {wallBusy ? "Updating..." : "Show me on the wall"}
                </button>
              </>
            ) : (
              <>
                On the public supporters list.{" "}
                <button
                  type="button"
                  className="donate-wall-toggle"
                  disabled={wallBusy}
                  onClick={() => setWallVisibility(true)}
                >
                  {wallBusy ? "Updating..." : "Hide me from the wall"}
                </button>
              </>
            )}
          </p>
        )}
        {!user && (
          <p className="portal-muted donate-status">
            Log in with Discord to see Essence gifts scaled to your profile.
          </p>
        )}

        {!configured && (
          <p className="donate-flash">
            Stripe keys are not set on the server yet. Add{" "}
            <code>STRIPE_SECRET_KEY</code> (and webhook secret) to enable
            checkout.
          </p>
        )}

        <div className="donate-currency-bar">
          <label htmlFor="donate-currency">Show prices in</label>
          <select
            id="donate-currency"
            className="donate-currency-select"
            value={displayCurrency}
            onChange={(e) => setDisplayCurrency(e.target.value)}
          >
            {DONATE_DISPLAY_CURRENCIES.map((c) => (
              <option key={c.code} value={c.code}>
                {c.label}
              </option>
            ))}
          </select>
          <span className="donate-currency-note">
            Checkout still charges in USD
            {displayCurrency !== "USD"
              ? ` · ~${formatDonateMoney(100, displayCurrency, fxRate)} per $1`
              : ""}
            {fxSource === "fallback" ? " · approx rates" : ""}
          </span>
        </div>

        {loading ? (
          <div className="page-loading">
            <div className="sync-spinner" />
          </div>
        ) : (
          <>
            <h2 className="donate-section-title">For you</h2>
            <div className="donate-grid">
              {tiers.map((tier) => {
                const priceLabel = priceFor(tier);
                const cta =
                  tier.kind === "subscription" ? "Subscribe" : "Donate once";
                return (
                  <article key={tier.id} className="donate-card">
                    <div className="donate-card-head">
                      <span className="donate-card-emoji" aria-hidden>
                        {tier.badgeEmoji || "☾"}
                      </span>
                      <h2>{tier.name}</h2>
                      <p className="donate-price">{priceLabel}</p>
                      {displayCurrency !== "USD" && (
                        <p className="donate-price-usd">
                          {tier.kind === "subscription"
                            ? `${formatDonateMoney(tier.amountCents, "USD", 1)}/mo USD`
                            : `${formatDonateMoney(tier.amountCents, "USD", 1)} USD`}
                        </p>
                      )}
                      <p className="donate-blurb">{tier.blurb}</p>
                      {tier.essenceGiftPreview != null && (
                        <p className="donate-essence-preview">
                          ~{Number(tier.essenceGiftPreview).toLocaleString()}{" "}
                          Essence
                          {tier.kind === "subscription"
                            ? " / payment"
                            : " gift"}
                        </p>
                      )}
                    </div>
                    <ul className="donate-perks">
                      {(tier.perks || []).map((line) => (
                        <li key={line}>{line}</li>
                      ))}
                    </ul>
                    <div className="donate-card-foot">
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={!configured || busy === tier.id}
                        onClick={() => startCheckout(tier.id)}
                      >
                        {busy === tier.id ? "Redirecting..." : cta}
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>

            <h2 className="donate-section-title">Season pass</h2>
            <div className="donate-grid">
              <article className="donate-card">
                <div className="donate-card-head">
                  <span className="donate-card-emoji" aria-hidden>
                    {nocturnePass.badgeEmoji || "🌑"}
                  </span>
                  <h2>{nocturnePass.name}</h2>
                  <p className="donate-price">{priceFor(nocturnePass)}</p>
                  {displayCurrency !== "USD" && (
                    <p className="donate-price-usd">
                      {formatDonateMoney(nocturnePass.amountCents, "USD", 1)} USD
                    </p>
                  )}
                  {nocturnePass.discounted && (
                    <p className="donate-essence-preview">
                      Supporter price · full{" "}
                      {formatDonateMoney(
                        nocturnePass.fullAmountCents || 299,
                        displayCurrency,
                        fxRate,
                      )}
                    </p>
                  )}
                  <p className="donate-blurb">{nocturnePass.blurb}</p>
                </div>
                <ul className="donate-perks">
                  {(nocturnePass.perks || []).map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
                <div className="donate-card-foot">
                  {nocturnePass.owned || nocturnePass.included ? (
                    <p className="portal-muted">
                      {nocturnePass.included
                        ? "Included with your Patron/Benefactor sub."
                        : "Premium unlocked for this season."}
                    </p>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={!configured || busy === "nocturne_pass"}
                      onClick={() => startCheckout("nocturne_pass")}
                    >
                      {busy === "nocturne_pass"
                        ? "Redirecting..."
                        : "Unlock premium"}
                    </button>
                  )}
                </div>
              </article>
            </div>

            <h2 className="donate-section-title">For your server</h2>
            <div className="donate-grid">
              <article className="donate-card">
                <div className="donate-card-head">
                  <span className="donate-card-emoji" aria-hidden>
                    {serverHost.badgeEmoji || "🏰"}
                  </span>
                  <h2>{serverHost.name}</h2>
                  <p className="donate-price">{priceFor(serverHost)}</p>
                  {displayCurrency !== "USD" && (
                    <p className="donate-price-usd">
                      {formatDonateMoney(serverHost.amountCents, "USD", 1)} USD
                    </p>
                  )}
                  <p className="donate-blurb">{serverHost.blurb}</p>
                </div>
                <ul className="donate-perks">
                  {(serverHost.perks || []).map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
                <div className="donate-card-foot">
                  <label
                    className="input-label"
                    htmlFor="server-host-guild"
                    style={{ display: "block", marginBottom: 8 }}
                  >
                    Server to unlock
                  </label>
                  <select
                    id="server-host-guild"
                    className="settings-input"
                    value={hostGuildId}
                    onChange={(e) => setHostGuildId(e.target.value)}
                    style={{ marginBottom: 12, width: "100%" }}
                  >
                    <option value="">
                      {manageableGuilds.length
                        ? "Select a server…"
                        : "Log in and sync a server with Kami"}
                    </option>
                    {manageableGuilds.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={
                      !configured ||
                      busy === "server_host" ||
                      !hostGuildId
                    }
                    onClick={() => startCheckout("server_host", hostGuildId)}
                  >
                    {busy === "server_host"
                      ? "Redirecting..."
                      : "Unlock once"}
                  </button>
                </div>
              </article>
            </div>
          </>
        )}

        <section className="donate-supporters" aria-labelledby="supporters-heading">
          <div className="donate-supporters-head">
            <h2 id="supporters-heading">Supporters of Nocturne</h2>
            <p>
              Everyone keeping the quiet lit. Thank you.
              {!supportersLoading && supporters.length > 0 && (
                <>
                  {" "}
                  · {supporters.length} total
                  {supporterCounts.benefactor > 0 && (
                    <> · {supporterCounts.benefactor} Benefactor</>
                  )}
                  {supporterCounts.patron > 0 && (
                    <> · {supporterCounts.patron} Patron</>
                  )}
                  {supporterCounts.supporter > 0 && (
                    <> · {supporterCounts.supporter} Supporter</>
                  )}
                </>
              )}
            </p>
          </div>

          {supportersLoading ? (
            <div className="page-loading">
              <div className="sync-spinner" />
            </div>
          ) : supporters.length === 0 ? (
            <p className="portal-muted donate-supporters-empty">
              No public supporters yet. Be the first on the wall.
            </p>
          ) : (
            <ul className="donate-supporters-list">
              {supporters.map((s) => {
                const since = formatSince(s.lastDonationAt);
                return (
                  <li
                    key={s.userId}
                    className={`donate-supporter-row tier-${s.tierId}`}
                  >
                    <img
                      className="donate-supporter-avatar"
                      src={s.avatarUrl}
                      alt=""
                      width={40}
                      height={40}
                      loading="lazy"
                    />
                    <div className="donate-supporter-meta">
                      <span className="donate-supporter-name">
                        {s.username}
                      </span>
                      {s.activeTitle && (
                        <span className="donate-supporter-title">
                          {s.activeTitle}
                        </span>
                      )}
                    </div>
                    <div className="donate-supporter-tier">
                      <span className="donate-supporter-badge">
                        <span aria-hidden>{s.tierEmoji}</span> {s.tierName}
                      </span>
                      <span className="donate-supporter-kind">
                        {s.kind === "monthly"
                          ? s.subscriptionActive
                            ? "Monthly"
                            : "Lifetime"
                          : "One-time"}
                        {since ? ` · since ${since}` : ""}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <p className="portal-muted donate-footnote">
          Payments are processed by Stripe. Personal tiers are for you;
          Server Host attaches to one Discord server. Discord boosts stay
          separate and do not unlock <code>/patron</code> or Server Host.
        </p>
      </div>
    </div>
  );
}
